// Soft-navigation (client route-change) latency (Better Stack RUM).
//
// App Router <Link> clicks and programmatic router navigations are SOFT
// navigations: they update the URL via the History API and swap RSC payloads
// without a full document load, so they never produce a navigation-timing
// entry or a `page-load` web event. The latency a user feels moving between
// routes is therefore otherwise unmeasured. We patch the History API — the
// single chokepoint every client navigation flows through — to mark a start
// timestamp, then measure to the next painted frame and beacon the duration.

import { clientNetworkTag } from './client';
import { routePerfNavigationPainted, routePerfNavigationStarted } from './route-performance';
import { beaconVitals } from './vitals-beacon';

/** Normalised route key: first three path segments (drops ids deeper down). */
function routeKey(pathname: string): string {
  return pathname.split('/').slice(0, 4).join('/');
}

export function initSoftNavigationTracking(): void {
  if (typeof window === 'undefined') return;

  const history = window.history;
  let currentPath = window.location.pathname;

  const measure = (toPath: string, start: number): void => {
    // Double rAF: the inner callback runs after the browser has painted the
    // post-navigation frame, so start → here ≈ route-change → painted. In the
    // common prefetched case the new route paints data-complete; in the cold
    // case it paints the route's loading skeleton — both are real user latency.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        routePerfNavigationPainted(toPath, performance.now());
        beaconVitals({
          kind: 'soft-nav',
          value: Math.round(performance.now() - start),
          network: clientNetworkTag(),
          toRoute: routeKey(toPath),
        });
      });
    });
  };

  const onNavigate = (toPath: string, navigationType: 'soft' | 'back_forward' = 'soft'): void => {
    // Ignore same-path History writes (query-param / scroll-state updates).
    if (toPath === currentPath) return;
    currentPath = toPath;
    const start = performance.now();
    routePerfNavigationStarted(toPath, start, navigationType);
    measure(toPath, start);
  };

  const patch = (key: 'pushState' | 'replaceState'): void => {
    const original = history[key].bind(history);
    // Derive the param tuple from the built-in signature so we neither widen
    // nor narrow it (the History state arg is intentionally untyped upstream).
    history[key] = (...args: Parameters<History['pushState']>): void => {
      original(...args);
      const url = args[2];
      if (url == null) return;
      try {
        onNavigate(new URL(String(url), window.location.href).pathname);
      } catch {
        /* malformed url — skip */
      }
    };
  };

  patch('pushState');
  patch('replaceState');
  window.addEventListener('popstate', () => {
    onNavigate(window.location.pathname, 'back_forward');
  });
}
